import { PreComputedTips } from 'public/components/online_ordering/types'

/**
 * Interface for tip options with percent and value
 */
export interface TipOption {
  percent: number
  value: number
}

/**
 * Filters and deduplicates tip options by percent value.
 * Removes duplicate tip percentages, keeping the first occurrence of each percentage.
 *
 * @param tipOptions - Array of precomputed tips that may contain duplicates
 * @returns Array of unique tip options filtered by percent value
 */
export const getUniqueTipOptions = (
  tipOptions: PreComputedTips
): TipOption[] => {
  if (!tipOptions || !Array.isArray(tipOptions)) {
    return []
  }

  // Filter out null/undefined tips and ensure they have valid percent and value
  const validTips = tipOptions.filter(
    (tip): tip is NonNullable<typeof tip> & TipOption =>
      !!tip && typeof tip.percent === 'number' && typeof tip.value === 'number'
  )

  // Use Map to deduplicate by percent value, keeping the first occurrence
  const uniqueTipsMap = new Map<number, TipOption>()

  for (const tip of validTips) {
    if (!uniqueTipsMap.has(tip.percent)) {
      uniqueTipsMap.set(tip.percent, tip)
    }
  }

  return Array.from(uniqueTipsMap.values())
}
